import type { LightMaterialComponent } from '../components/LightShaderComponent.js';
import type { Nullable } from '../../../utils/Utils.js';
import type {
    ShaderComponent,
    ShaderInputDescriptor,
    UniformArrayDescriptor,
    ShaderInfo,
    VaryArrayDescriptor,
} from '../Shader.js';
import { WebGLShaderDataType } from '../../webgl/WGLConstants.js';
import {
    mapAttributeType,
    mapInnerGlobalUniform,
    createUniformArrays,
    createUniforms,
    createVertVaryings,
    createHeader,
    expandShaderExtension,
    createAttributes,
    setUpPrecision,
    createFragVaryings,
    createUBOs,
    ShaderExtensionTypes,
    ShaderAttributeTypes,
    ShaderVaryingTypes,
    ShaderInjectionTypes,
} from './ShaderHelper.js';
import type { ShaderBlock } from './ShaderBlock.js';
import { ShaderBlockPool } from './ShaderBlockPool.js';
import type { UniformBlockObject } from '../components/UniformBlockObject.js';
import { Capabilities } from '../../Capabilities.js';
import { BuiltInUniformTypes } from '../../RenderState/BuiltInUniforms.js';
import { logger } from '../../../utils/Logger.js';
import { gles2_polyfill } from './GLES2_Polyfill.js';

export { ShaderExtensionTypes, ShaderAttributeTypes, ShaderInjectionTypes, ShaderVaryingTypes };
// ShaderBuilder provides all functions that is used to assemble shader components to a completed shader code;
// Both of vertex and fragment shaders' code can be generated by function build().
// Uniform, attribute and varying of shader will be added to shader according to different attributes and settings
// of the object.
// This class can also set other options, such as UBOs, extensions and light and so on.
// Especially for the UBO, the UBO manager will roll back to normal uniforms if UBO is not supported.
export enum FragOutType {
    Float = 'float',
    Vec2 = 'vec2',
    Vec3 = 'vec3',
    Vec4 = 'vec4',
    UInt = 'uint',
    UVec4 = 'uvec4',
}
interface FragOut {
    name: string;
    type: FragOutType;
}

export class ShaderBuilder {
    lightComponent: Nullable<LightMaterialComponent> = null;

    private extensions = new Set<ShaderExtensionTypes>();
    private isInstanceMeshEnabled = false;
    private fragDefines: string[] = [];
    private postShaderStrProc: Array<(input: string) => string> = [];

    private components: ShaderComponent[] = [];
    private componentsMap: Map<string, ShaderComponent> = new Map();
    private attributes: Set<ShaderAttributeTypes> = new Set();
    private attributesCustom: ShaderInputDescriptor[] = [];
    private instanceAttributes: ShaderInputDescriptor[] = [];

    private vertexes: Set<ShaderBlock> = new Set();
    private vertexesCustom: string[] = [];

    private globalUniforms: Set<BuiltInUniformTypes> = new Set();

    private uniforms: ShaderInputDescriptor[] = [];
    private uniformArrays: UniformArrayDescriptor[] = [];

    private varyings: Set<ShaderVaryingTypes> = new Set();
    private varyingsCustom: ShaderInputDescriptor[] = [];
    private varyingsCustomArray: VaryArrayDescriptor[] = [];

    private fragOut: FragOut[] = [{ name: 'pc_fragColor', type: FragOutType.Vec4 }];
    private fragments: Set<ShaderBlock> = new Set();
    private fragmentsCustom: string[] = [];
    private injections: Map<ShaderInjectionTypes, string[]> = new Map();

    private uniformBlocks: UniformBlockObject[] = [];

    addDefaultFragColor = true;
    flatShadingNormal = false;

    static spawn() {
        return new ShaderBuilder();
    }
    constructor() {
        this.addDefaultAttribute(ShaderAttributeTypes.position);
    }

    setFragOutputChannel(fragOut: FragOut[]) {
        this.fragOut = fragOut;
        return this;
    }

    addNewFragOutputChannel(name: string, type = FragOutType.Vec4) {
        this.fragOut.push({ name, type });
        return this;
    }

    addUBO(ubo: UniformBlockObject) {
        this.uniformBlocks.push(ubo);
        return this;
    }

    addExtension(re: ShaderExtensionTypes) {
        this.extensions.add(re);
        return this;
    }

    addLight(l: LightMaterialComponent) {
        if (this.lightComponent !== null) {
            logger.unreachable('lightAble has already registered');
        }
        this.lightComponent = l;
        return this;
    }

    hasComponent(name: string) {
        return this.componentsMap.has(name);
    }

    getComponent(name: string) {
        return this.componentsMap.get(name);
    }

    extend(sc: ShaderComponent | null) {
        if (sc === null) {
            return this;
        }
        sc.extendShaderShape(this);
        sc.extendShaderShading(this);
        this.components.push(sc);
        this.componentsMap.set(sc.className(), sc);
        return this;
    }

    addInstanceAttribute(name: string, type: WebGLShaderDataType) {
        this.instanceAttributes.push({ name, type });
        return this;
    }

    addCustomAttribute(name: string, type: WebGLShaderDataType) {
        this.attributesCustom.push({ name, type });
        return this;
    }

    addDefaultAttribute(att: ShaderAttributeTypes) {
        this.attributes.add(att);
        return this;
    }

    generateAttributes() {
        return this.instanceAttributes
            .slice()
            .concat(Array.from(this.attributes).map(mapAttributeType))
            .concat(this.attributesCustom.slice());
    }

    private getVertexesString() {
        return Array.from(this.vertexes)
            .map(i => i.source)
            .concat(this.vertexesCustom)
            .join('\n');
    }

    addVertex(lib: ShaderBlock) {
        this.vertexes.add(lib);
        return this;
    }

    addVertexCustom(lib: string) {
        this.vertexesCustom.push(lib);
        return this;
    }

    private getFragmentsString() {
        return Array.from(this.fragments)
            .map(i => i.source)
            .concat(this.fragmentsCustom)
            .join('\n');
    }

    addFragmentCustom(s: string) {
        this.fragmentsCustom.push(s);
        return this;
    }

    addFragment(lib: ShaderBlock) {
        this.fragments.add(lib);
        return this;
    }

    addUniform(name: string, type: WebGLShaderDataType) {
        this.uniforms.push({ name, type });
        return this;
    }
    addUniformArray(name: string, type: WebGLShaderDataType, length: number) {
        this.uniformArrays.push({ des: { name, type }, length });
        return this;
    }

    addUniformStruct(_name: string, _value: any) {
        return this;
    }

    addUniformStructArray(_name: string, _value: any, _length: number) {
        return this;
    }

    addGlobalUniform(g: BuiltInUniformTypes) {
        this.globalUniforms.add(g);
        return this;
    }

    private generateGlobalUniforms() {
        const globalUniforms = Array.from(this.globalUniforms);
        const uniforms: ShaderInputDescriptor[] = [];
        const uniformArrays: UniformArrayDescriptor[] = [];
        for (let i = 0; i < globalUniforms.length; i++) {
            const uniform = mapInnerGlobalUniform(globalUniforms[i]);
            if ((uniform as any).length !== undefined) {
                uniformArrays.push(uniform as UniformArrayDescriptor);
            } else {
                uniforms.push(uniform as ShaderInputDescriptor);
            }
        }
        return { uniforms, uniformArrays };
    }

    addVaryingCustomArray(name: string, type: WebGLShaderDataType, length: number) {
        this.varyingsCustomArray.push({ des: { name, type }, length });
        return this;
    }

    addVaryingCustom(name: string, type: WebGLShaderDataType) {
        this.varyingsCustom.push({ name, type });
        return this;
    }

    addVarying(v: ShaderVaryingTypes) {
        if (!this.varyings.has(v)) {
            this.expandVaryingSupport(v);
        }
        this.varyings.add(v);
        return this;
    }

    private expandVaryingSupport(v: ShaderVaryingTypes) {
        switch (v) {
            case ShaderVaryingTypes.fragNormal:
                this.addDefaultAttribute(ShaderAttributeTypes.normal);

                if (this.isInstanceMeshEnabled) {
                    this.inject(ShaderInjectionTypes.vary_normal, ShaderBlockPool.NormalInstance).addGlobalUniform(
                        BuiltInUniformTypes.viewMatrix,
                    );
                } else {
                    this.inject(ShaderInjectionTypes.vary_normal, ShaderBlockPool.Normal).addGlobalUniform(
                        BuiltInUniformTypes.normalMatrix,
                    );
                }

                break;
            case ShaderVaryingTypes.fragUV:
                this.addDefaultAttribute(ShaderAttributeTypes.uv);
                this.inject(ShaderInjectionTypes.vary_uv, `vUv = vec3(uv, 1.0).xy;`);
                break;
            case ShaderVaryingTypes.viewPosition:
                this.inject(ShaderInjectionTypes.vary_viewPosition, 'vViewPosition = - mvPosition.xyz;');
                break;
            case ShaderVaryingTypes.worldPosition:
                if (this.isInstanceMeshEnabled) {
                    this.inject(
                        ShaderInjectionTypes.vary_worldPosition,
                        'vWorldPosition = (instanceMatrix*vec4( position, 1.0 )).xyz;',
                    );
                } else {
                    this.inject(
                        ShaderInjectionTypes.vary_worldPosition,
                        'vWorldPosition = (modelMatrix*vec4( position, 1.0 )).xyz;',
                    ).addGlobalUniform(BuiltInUniformTypes.modelMatrix);
                }
                break;
            case ShaderVaryingTypes.vertexColor:
                this.addDefaultAttribute(ShaderAttributeTypes.color);
                this.inject(ShaderInjectionTypes.vary_any, 'vColor = color;');
                break;
            default:
                break;
        }
    }

    private generateVaryings() {
        return this.varyingsCustom.slice().concat(
            Array.from(this.varyings).map(varying => {
                switch (varying) {
                    case ShaderVaryingTypes.fragNormal:
                        return { name: 'vNormal', type: WebGLShaderDataType.Vec3 };
                    case ShaderVaryingTypes.fragUV:
                        return { name: 'vUv', type: WebGLShaderDataType.Vec2 };
                    case ShaderVaryingTypes.viewPosition:
                        return { name: 'vViewPosition', type: WebGLShaderDataType.Vec3 };
                    case ShaderVaryingTypes.worldPosition:
                        return { name: 'vWorldPosition', type: WebGLShaderDataType.Vec3 };
                    case ShaderVaryingTypes.vertexColor:
                        return { name: 'vColor', type: WebGLShaderDataType.Vec3 };
                }
            }),
        );
    }

    addFragDefine(de: string) {
        this.fragDefines.push(de);
        return this;
    }

    private getInjectionString(point: ShaderInjectionTypes) {
        const prev = this.injections.get(point);
        if (prev === undefined) {
            return '\n';
        } else {
            return prev.join('\n') + '\n';
        }
    }

    when(prediction: boolean, c: (builder: ShaderBuilder) => ShaderBuilder) {
        return prediction ? c(this) : this;
    }

    inject(point: ShaderInjectionTypes, shader: string) {
        const prev = this.injections.get(point);
        if (prev === undefined) {
            this.injections.set(point, [shader]);
        } else {
            prev.push(shader);
        }

        if (point === ShaderInjectionTypes.frag_normal) {
            this.addVarying(ShaderVaryingTypes.fragNormal);
        }
        return this;
    }

    useCamera(useInstance?: boolean) {
        if (useInstance === true) {
            this.isInstanceMeshEnabled = true;
        }

        if (useInstance === true) {
            this.addInstanceAttribute('mcol0', WebGLShaderDataType.Vec3)
                .addInstanceAttribute('mcol1', WebGLShaderDataType.Vec3)
                .addInstanceAttribute('mcol2', WebGLShaderDataType.Vec3)
                .addInstanceAttribute('mcol3', WebGLShaderDataType.Vec3)
                .addGlobalUniform(BuiltInUniformTypes.projectionMatrix)
                .addGlobalUniform(BuiltInUniformTypes.viewMatrix)
                .inject(ShaderInjectionTypes.gl_Position, ShaderBlockPool.ProjectionInstance);
        } else {
            this.addGlobalUniform(BuiltInUniformTypes.projectionMatrix)
                .addGlobalUniform(BuiltInUniformTypes.modelViewMatrix)
                .inject(ShaderInjectionTypes.gl_Position, ShaderBlockPool.Projection);
        }
        return this;
    }

    useComponent(sc: ShaderComponent) {
        sc.extendShaderShape(this);
        sc.extendShaderShading(this);
    }

    addShaderStrProcess(f: (input: string) => string) {
        this.postShaderStrProc.push(f);
        return this;
    }
    private processPostShaderString(s: string) {
        let str = s;
        this.postShaderStrProc.forEach(f => {
            str = f(str);
        });
        return str;
    }

    private getFragOutShaderHeader() {
        let result = '';
        if (Capabilities.IS_WEBGL2) {
            this.fragOut.forEach((f, i) => {
                result += `layout(location=${i}) out ${f.type} ${f.name};\n`;
            });
            result += '#define gl_FragColor pc_fragColor\n';
        } else {
            if (this.fragOut.length > 1) {
                logger.unsupported('egs mrt only support in WEBGL2');
            }
        }
        return result;
    }

    build(): ShaderInfo {
        if (this.flatShadingNormal) {
            // make sure viewPosition exist
            this.addVarying(ShaderVaryingTypes.viewPosition);
            this.addExtension(ShaderExtensionTypes.derivatives);
        }

        let injectVaryingsStr = '';
        injectVaryingsStr += this.getInjectionString(ShaderInjectionTypes.vary_normal);
        injectVaryingsStr += this.getInjectionString(ShaderInjectionTypes.vary_uv);
        injectVaryingsStr += this.getInjectionString(ShaderInjectionTypes.vary_viewPosition);
        injectVaryingsStr += this.getInjectionString(ShaderInjectionTypes.vary_worldPosition);

        const injectVaryings = this.injections.get(ShaderInjectionTypes.vary_any);
        if (injectVaryings !== undefined) {
            injectVaryingsStr += injectVaryings.join('\n\n');
        }

        const attributes = this.generateAttributes();
        const globalUniforms = this.generateGlobalUniforms();
        const uniforms = [...this.uniforms, ...globalUniforms.uniforms];
        const uniformArrays = [...this.uniformArrays, ...globalUniforms.uniformArrays];
        const uniformStr =
            createUniforms(uniforms) +
            createUniformArrays(uniformArrays) +
            createUBOs(this.uniformBlocks, Capabilities.IS_WEBGL2);
        const varyings = this.generateVaryings();
        const varyVertStr = createVertVaryings(varyings, this.varyingsCustomArray, Capabilities.IS_WEBGL2);
        const varyFragStr = createFragVaryings(varyings, this.varyingsCustomArray, Capabilities.IS_WEBGL2);
        const vertex = `${createHeader(Capabilities.IS_WEBGL2)}
${expandShaderExtension(this.extensions, Capabilities.IS_WEBGL2)}
${createAttributes(attributes, Capabilities.IS_WEBGL2)}
${uniformStr}
${varyVertStr}

${ShaderBlockPool.ShaderConst}
${Capabilities.IS_WEBGL2 ? '' : '#define GLES2_POLYFILL'}
${this.getVertexesString()}

void main() {
    vec3 position = position;
    vec4 mvPosition = vec4(position, 1.0);
    ${this.getInjectionString(ShaderInjectionTypes.position)}

    gl_Position = vec4(position, 1.0);
    ${this.getInjectionString(ShaderInjectionTypes.gl_Position)}

    ${this.getInjectionString(ShaderInjectionTypes.gl_PointSize)}

    ${injectVaryingsStr}
}
`;
        const normal = !this.varyings.has(ShaderVaryingTypes.fragNormal)
            ? ''
            : `
            vec3 normal = normalize(vNormal);

            ${
                this.flatShadingNormal
                    ? `
                vec3 fdx = vec3( dFdx( vViewPosition.x ), dFdx( vViewPosition.y ), dFdx( vViewPosition.z ) );
                vec3 fdy = vec3( dFdy( vViewPosition.x ), dFdy( vViewPosition.y ), dFdy( vViewPosition.z ) );
                normal = normalize( cross( fdx, fdy ) );
                `
                    : ``
            }
            ${`
                #ifdef DOUBLE_SIDE
                  float faceDirection = gl_FrontFacing ? 1.0 : -1.0;
                  normal = normal * faceDirection;
                #endif
                `}
            ${this.getInjectionString(ShaderInjectionTypes.frag_normal)}
        `;

        const opacity = !this.injections.has(ShaderInjectionTypes.channel_alpha)
            ? ''
            : `
            float opacity = 1.0;
            ${this.getInjectionString(ShaderInjectionTypes.channel_alpha)}
        `;

        const color = !this.injections.has(ShaderInjectionTypes.channel_color)
            ? ''
            : `
            vec3 color = vec3(1.0);
            ${this.getInjectionString(ShaderInjectionTypes.channel_color)}
        `;

        const ao = !this.injections.has(ShaderInjectionTypes.channel_ao)
            ? ''
            : `
            float ao = 0.0;
            ${this.getInjectionString(ShaderInjectionTypes.channel_ao)}
        `;

        const lightMap = !this.injections.has(ShaderInjectionTypes.channel_lightMap)
            ? ''
            : `
            float lightMap = 0.0;
            ${this.getInjectionString(ShaderInjectionTypes.channel_lightMap)}
        `;

        const specular = !this.injections.has(ShaderInjectionTypes.channel_specular)
            ? ''
            : `
            vec3 specular = vec3(1.0);
            ${this.getInjectionString(ShaderInjectionTypes.channel_specular)}
        `;

        const roughness = !this.injections.has(ShaderInjectionTypes.channel_roughness)
            ? ''
            : `
        float roughness = 0.0;
        ${this.getInjectionString(ShaderInjectionTypes.channel_roughness)}`;

        const metalness = !this.injections.has(ShaderInjectionTypes.channel_metalness)
            ? ''
            : `
        float metalness = 0.0;
        ${this.getInjectionString(ShaderInjectionTypes.channel_metalness)}`;

        const specularStrength = !this.injections.has(ShaderInjectionTypes.channel_specularStrength)
            ? ''
            : `
        float specularStrength = 0.0;
        ${this.getInjectionString(ShaderInjectionTypes.channel_specularStrength)}`;

        const shininess = !this.injections.has(ShaderInjectionTypes.channel_shininess)
            ? ''
            : `
        float shininess = 0.0;
        ${this.getInjectionString(ShaderInjectionTypes.channel_shininess)}`;

        const frag = `${createHeader(Capabilities.IS_WEBGL2)}
${expandShaderExtension(this.extensions, Capabilities.IS_WEBGL2)}
${setUpPrecision()}
${this.getFragOutShaderHeader()}
${this.fragDefines.join('\n')}
${uniformStr}
${varyFragStr}

${ShaderBlockPool.ShaderConst}
${Capabilities.IS_WEBGL2 ? '' : '#define GLES2_POLYFILL'}
${this.getFragmentsString()}

void main() {
    ${this.getInjectionString(ShaderInjectionTypes.discard)}

    ${normal}
    ${opacity}
    ${color}
    ${ao}
    ${lightMap}
    ${specular}
    ${roughness}
    ${metalness}
    ${specularStrength}
    ${shininess}

    ${this.addDefaultFragColor ? 'gl_FragColor = vec4(0.0);' : ''}
    ${this.getInjectionString(ShaderInjectionTypes.gl_FragColor)}
    ${this.getInjectionString(ShaderInjectionTypes.gl_FragColorModify)}
    ${this.getInjectionString(ShaderInjectionTypes.gl_FragDepthEXT)}
    ${this.getInjectionString(ShaderInjectionTypes.frag_any)}
}
`;

        if (!Capabilities.IS_WEBGL2) {
            this.addShaderStrProcess(gles2_polyfill);
        }
        return {
            uniforms,
            uniformArrays: this.uniformArrays.slice(),
            uniformsBlocks: this.uniformBlocks.map(u => u.getDescriptor()),
            globalUniforms: Array.from(this.globalUniforms),
            attributes,
            varyings: this.varyingsCustom.slice(),
            vertex: this.processPostShaderString(vertex),
            frag: this.processPostShaderString(frag),
        };
    }
}
